import { LuIcon, Text } from "@overmap-ai/blocks"
import type { Report } from "@overmap-ai/common"
import {
	type ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table"
import { useMemo } from "react"

import { reportStatusData } from "../../../mappings/reports"
import { ReportDownloadButton } from "../../Buttons/ReportDownloadButton"
import { UserIcon } from "../../Icons/UserIcon"
import { ColumnSortButton, ColumnSortIcon } from "../components"

export const useReportTableColumns = () => {
	return useMemo<ColumnDef<Report>[]>(
		() => [
			{
				id: "created_by",
				accessorFn: (report) => report.created_by,
				meta: { plainHeader: "Creator" },
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Creator
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: function Cell({ getValue }) {
					const userId = getValue<Report["created_by"]>()

					return <UserIcon size="sm" userId={userId ?? undefined} />
				},
			},
			{
				id: "name",
				accessorFn: (report) => report.name,
				meta: { plainHeader: "Name" },
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Name
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ row }) => <Text className="truncate">{row.original.name}</Text>,
			},
			{
				id: "created_at",
				accessorFn: (report) => new Date(report.created_at).getTime(),
				meta: { plainHeader: "Created at" },
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Created at
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ row }) => (
					<Text accentColor="base" className="whitespace-nowrap">
						{new Date(row.original.created_at).toLocaleString()}
					</Text>
				),
			},
			{
				id: "status",
				accessorFn: (report) => report.status,
				meta: { plainHeader: "Status" },
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							Status
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell: ({ row }) => {
					const { icon, color, label } = reportStatusData[row.original.status]
					return (
						<Text accentColor="base" className="whitespace-nowrap">
							<LuIcon icon={icon} color={color} className="inline-block align-middle" />
							&nbsp;
							{label}
						</Text>
					)
				},
			},
			{
				id: "download",
				header: "",
				cell: ({ row }) => <ReportDownloadButton report={row.original} />,
			},
		],
		[],
	)
}

export const useReportTable = (args: Omit<TableOptions<Report>, "getCoreRowModel">) => {
	return useReactTable<Report>({
		...args,
		getRowId: (report) => report.uuid,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		initialState: { sorting: [{ id: "created_at", desc: true }] },
	})
}
